import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { transformReportData, type BackendReportData } from '../../utils/reportDataTransformers';

// Table components
import Cuadro04 from './tables/Cuadro04';
import Cuadro05 from './tables/Cuadro05';
import Anexo1 from './tables/Anexo1';

// Chart components (v2 - matching PDF report format)
import Grafico04 from './charts/Grafico04';
import Grafico05 from './charts/Grafico05';
import Grafico06 from './charts/Grafico06';
import Grafico07V2 from './charts/Grafico07_v2';
import Grafico08V2 from './charts/Grafico08_v2';
import Grafico09V2 from './charts/Grafico09_v2';
import Grafico10V2 from './charts/Grafico10_v2';

import './ReportPreview.css';

export interface ReportPreviewProps {
  reportData: BackendReportData | null;
  selectedSections: {
    estructura_balance?: boolean;
    indicadores_financieros?: boolean;
    anexo_1?: boolean;
  };
}

/**
 * Report Preview Component
 *
 * Renders the complete report with all selected sections, charts, and tables
 * Uses data from backend API transformed into component-specific formats
 */
export const ReportPreview: React.FC<ReportPreviewProps> = ({
  reportData,
  selectedSections
}) => {
  const [transformedData, setTransformedData] = useState<ReturnType<typeof transformReportData> | null>(null);

  useEffect(() => {
    if (reportData) {
      const transformed = transformReportData(reportData);
      setTransformedData(transformed);
    }
  }, [reportData]);

  if (!reportData || !transformedData) {
    return (
      <div className="report-preview-container">
        <div className="report-preview-empty">
          <p>No hay datos de reporte disponibles. Genere un reporte para ver la vista previa.</p>
        </div>
      </div>
    );
  }

  const { metadata } = reportData;
  const sections = selectedSections;

  return (
    <div className="report-preview-container">
      {/* Report Header */}
      <div className="report-header">
        <h1 className="report-title">Informe de Calificación de Riesgo</h1>
        <div className="report-metadata">
          <p><strong>Institución:</strong> {metadata.institution_id}</p>
          <p><strong>Período:</strong> {metadata.period}</p>
          <p><strong>Fecha de generación:</strong> {new Date(metadata.generation_date).toLocaleDateString('es-EC')}</p>
        </div>
      </div>

      {/* Estructura de Balance Section */}
      {sections.estructura_balance && (
        <section className="report-section" id="estructura-balance">
          <h2 className="section-title">Estructura de Balance</h2>

          {/* Activo Subsection - Ingresos/Activo Productivo */}
          <div className="subsection">
            <h3 className="subsection-title">Activo</h3>
            {transformedData.narratives.activo && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.activo}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico04_new && transformedData.grafico04_new.length > 0 && (
              <div id="grafico-04">
                <Grafico04 data={transformedData.grafico04_new} />
              </div>
            )}
          </div>

          {/* Pasivo Subsection - Intereses Causados/Activo Productivo */}
          <div className="subsection">
            <h3 className="subsection-title">Pasivo</h3>
            {transformedData.narratives.pasivo && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.pasivo}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico05_new && transformedData.grafico05_new.length > 0 && (
              <div id="grafico-05">
                <Grafico05 data={transformedData.grafico05_new} />
              </div>
            )}
          </div>

          {/* Patrimonio Subsection - Composición del Patrimonio */}
          <div className="subsection">
            <h3 className="subsection-title">Patrimonio</h3>
            {transformedData.narratives.patrimonio && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.patrimonio}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico06_new && transformedData.grafico06_new.length > 0 && (
              <div id="grafico-06">
                <Grafico06 data={transformedData.grafico06_new} />
              </div>
            )}
          </div>

          {/* Cuadro 04 - Balance Evolution (after all subsections) */}
          <div className="subsection">
            <Cuadro04 data={transformedData.cuadro04} />
          </div>
        </section>
      )}

      {/* Indicadores Financieros Section */}
      {sections.indicadores_financieros && (
        <section className="report-section" id="indicadores-financieros">
          <h2 className="section-title">Indicadores Financieros</h2>

          {/* Solvencia Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Solvencia</h3>
            {transformedData.narratives.solvencia && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.solvencia}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico07_new && transformedData.grafico07_new.length > 0 && (
              <div id="grafico-07">
                <Grafico07V2 data={transformedData.grafico07_new} />
              </div>
            )}
          </div>

          {/* Liquidez Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Liquidez</h3>
            {transformedData.narratives.liquidez && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.liquidez}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico08_new && transformedData.grafico08_new.length > 0 && (
              <div id="grafico-08">
                <Grafico08V2 data={transformedData.grafico08_new} />
              </div>
            )}
            {transformedData.cuadro05.length > 0 && (
              <Cuadro05 data={transformedData.cuadro05} />
            )}
          </div>

          {/* Calidad de Activos Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Calidad de Activos</h3>
            {transformedData.narratives.calidad_activos && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.calidad_activos}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico09_new && transformedData.grafico09_new.length > 0 && (
              <div id="grafico-09">
                <Grafico09V2 data={transformedData.grafico09_new} />
              </div>
            )}
          </div>

          {/* Eficiencia y Gestión Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Eficiencia y Gestión</h3>
            {transformedData.narratives.eficiencia_gestion && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.eficiencia_gestion}
                </ReactMarkdown>
              </div>
            )}
            {transformedData.grafico10_new && transformedData.grafico10_new.length > 0 && (
              <div id="grafico-10">
                <Grafico10V2 data={transformedData.grafico10_new} />
              </div>
            )}
          </div>

          {/* Rentabilidad Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Rentabilidad</h3>
            {transformedData.narratives.rentabilidad && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.rentabilidad}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Riesgo de Interés Estructural Subsection */}
          <div className="subsection">
            <h3 className="subsection-title">Riesgo de Interés Estructural</h3>
            {transformedData.narratives.riesgo_interes && (
              <div className="narrative-text">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {transformedData.narratives.riesgo_interes}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Anexo 1 Section */}
      {sections.anexo_1 && (
        <section className="report-section" id="anexo-1">
          <Anexo1 data={transformedData.anexo1} />
        </section>
      )}

      {/* Report Footer */}
      <div className="report-footer">
        <p className="footer-disclaimer">
          Este informe ha sido generado automáticamente por el sistema de calificación de riesgo BankingLATAM.
          Los datos provienen de los estados financieros reportados por la institución a la Superintendencia
          de Economía Popular y Solidaria (SEPS) del Ecuador.
        </p>
        <p className="footer-timestamp">
          Generado el {new Date(metadata.generation_date).toLocaleString('es-EC')}
        </p>
      </div>
    </div>
  );
};

export default ReportPreview;
